import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { addSensorAPI, deleteSensor, getSensorData, getTempestToken, SensorPayload, updateSensor, updateSensorData } from '../../api/SensorDetailsAPIService';
// import ReactSwitch from 'react-switch';
import DeleteIcon from '../../assets/icons/DeleteIcon'
import Pen from '../../assets/icons/edit.svg'
import Cross from '../../assets/cross-icon.svg'
import Toast, { toastPosition, toastType } from '../Toast/Toast'
import MainNavMenu from '../../menus/MainNavMenu/MainNavMenu'
import { Route, useSearchParams } from 'react-router-dom'
import useErrorStatus from '../../hooks/UseErrorStatus'
import { ErrorTimeouts } from '../../constants/ErrorTimeouts'
import { useAuth0 } from '@auth0/auth0-react'
import { getUser } from '../../utils/auth.util'
import messagealert from '../../assets/message-alert-square.svg'
import Save from '../../assets/save.svg'
import { confirmWithContent } from '../../hooks/useConfirm'
import { AddSensorComponent } from './AddSensorComponent'
import SensorTableSkeleton from './SensorTableSkeleton'

interface SensorDetails {
  identity: string
  sensorType: 'Tempest' | 'Intellisense'
  isPublic: boolean
  edit: boolean
}

function SensorDetails() {
  const [fireAuthAPI, setAuthCheck] = useState(true)
  const [seed, setSeed] = useState(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [inputRows, setInputRows] = useState<Array<SensorDetails>>([
    {
      sensorType: 'Intellisense',
      identity: '',
      isPublic: false,
      edit: false,
    },
  ])
  const errorStatus = useErrorStatus()
  const { isAuthenticated } = useAuth0()

  const getOAuthToken = async (code: string) => {
    setAuthCheck(false)
    const url = 'https://swd.weatherflow.com/id/oauth2/token'
    const formData = new URLSearchParams()
    formData.append('grant_type', 'authorization_code')
    formData.append('code', code)
    formData.append('client_id', '664e5921-1eab-40d6-b123-70b6c279a62e')
    formData.append('client_secret', '88de8320-f7e3-49b4-a8bd-da1e92e30ecc')

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      })
      if (!response.ok) {
        throw new Error('OAuth request failed')
      }
      const data = await response.json()
      const sensorDataBody: SensorPayload = {
        sensorType: 'Tempest',
        identity: data['access_token'],
        isPublic: false,
      }
      const tempestAddSensorResp = await addSensorAPI(sensorDataBody)
      setInputRows([...tempestAddSensorResp])
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const [searchParams] = useSearchParams()
  const tempestCode = searchParams.get('code')
  if (tempestCode !== null && fireAuthAPI) {
    getOAuthToken(tempestCode)
  }

  const deleteInputRow = async (index: number) => {
    setIsLoading(true)
    try {
      const sensorID = inputRows[index].identity
      const response = await deleteSensor({ sensorID })
      setInputRows([...response])
      errorStatus.addMessage(
        `Sensor with ID : ${sensorID} was deleted!`,
        200,
        toastType.success,
        false,
        10000
      )
      setIsLoading(false)
    } catch (error) {
      errorStatus.addMessage(error.message, 400, toastType.error, false, 10000)
      setIsLoading(false)
    }
  }

  const updateSensorDetails = async (index: number) => {
    setIsLoading(true)
    try {
      const sensorBody: SensorPayload = {
        identity: inputRows[index].identity,
        sensorType: inputRows[index].sensorType,
        isPublic: inputRows[index].isPublic,
      }
      const response = await updateSensor(sensorBody)
      setInputRows([...response])
      errorStatus.addMessage(
        `Sensor with ID : ${sensorBody.identity} was updated!`,
        200,
        toastType.success,
        false,
        10000
      )
      setIsLoading(false)
    } catch (error) {
      errorStatus.addMessage(error.message, 400, toastType.error, false, 10000)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isAuthenticated && getUser()) {
      initSensorData()
    }
  }, [seed, isAuthenticated, getUser()])

  const handleSensorTypeChange = (
    index: number,
    value: 'Tempest' | 'Intellisense'
  ) => {
    const updatedRows = [...inputRows]
    updatedRows[index].sensorType = value
    setInputRows(updatedRows)
  }

  const handleisPublicChange = (index: number, value: boolean) => {
    const updatedRows = [...inputRows]
    updatedRows[index].isPublic = value
    setInputRows(updatedRows)
  }
  const handleEditChange = (index: number, value: boolean) => {
    const updatedRows = [...inputRows]
    updatedRows[index].edit = value
    setInputRows(updatedRows)
  }

  const cancelEdit = (index: number) => {
    const promise = getSensorData()
    promise.then((res) => {
      const updatedRows = [...inputRows]
      if (index < res.length) updatedRows[index] = res[index]
      else
        updatedRows[index] = {
          sensorType: 'Intellisense',
          identity: '',
          isPublic: false,
          edit: false,
        }
      setInputRows(updatedRows)
    })
  }

  const initSensorData = () => {
    setIsLoading(true)
    const promise = getSensorData()
    promise.then((res) => {
      setInputRows(res)
      setIsLoading(false)
    })
  }

  const addNewSensor = async () => {
    const updatedSensorDetails = await confirmWithContent<SensorPayload>({
      title: 'Add New Sensor',
      Content: AddSensorComponent,
      isMissionCASTExport: false,
      showControls: true,
    })

    if (updatedSensorDetails) {
      setIsLoading(true)
      try {
        const response = await addSensorAPI(updatedSensorDetails)
        setInputRows([...response])
        errorStatus.addMessage(
          `Sensor with ID : ${updatedSensorDetails.identity} was added!`,
          200,
          toastType.success,
          false,
          10000
        )
      } catch (error) {
        errorStatus.addMessage(
          error.message,
          400,
          toastType.error,
          false,
          10000
        )
      } finally {
        setIsLoading(false)
      }
    }
  }

  const renderTableRows = useCallback(() => {
    return (
      <tbody>
        {inputRows.map((row, index) => (
          <tr
            key={index}
            className="border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            <th
              scope="row"
              className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
            >
              {!row.edit ? (
                row.sensorType
              ) : (
                <select
                  className="block rounded-lg appearance-none w-full bg-white border border-gray-400 hover:border-gray-500 px-2 py-2 rounded shadow leading-tight focus:outline-none focus:shadow-outline"
                  value={row.sensorType}
                  onChange={(e) =>
                    handleSensorTypeChange(index, e.target.value)
                  }
                >
                  <option value="Intellisense">Intellisense</option>
                  <option value="Tempest">Tempest</option>
                </select>
              )}
            </th>
            <td className="px-6 py-4">{row.identity}</td>
            <td className="px-6 py-4">
              {!row.edit
                ? row.isPublic
                  ? 'true'
                  : 'false'
                : row.sensorType === 'Tempest' && (
                    <div className="relative inline-block w-10 align-middle select-none transition duration-200 ease-in">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          value=""
                          className="sr-only peer"
                          checked={row.isPublic}
                          onChange={() =>
                            handleisPublicChange(index, !row.isPublic)
                          }
                        />
                        <div className="w-11 h-6 bg-gray-400 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  )}
            </td>
            <td className="px-6 py-4">
              {!row.edit ? (
                <button
                  className="block appearance-none w-full px-4 py-2 leading-tight"
                  onClick={() => handleEditChange(index, !row.edit)}
                  title="Edit Token"
                >
                  <img className="w-6 h-6" src={Pen} alt="" />
                </button>
              ) : (
                <div className="flex flex-row">
                  <button
                    className="inline-block appearance-none w-full leading-tight"
                    onClick={() => updateSensorDetails(index)}
                  >
                    <img className="w-6 h-6" src={Save} alt="save button" />
                  </button>
                  <button
                    className="inline-block appearance-none w-full leading-tight"
                    onClick={() => cancelEdit(index)}
                  >
                    <img className="w-6 h-6" src={Cross} alt="cancel button" />
                  </button>
                </div>
              )}
            </td>
            <td className="px-6 py-4 text-right">
              <button
                className="block appearance-none w-full px-4 py-2 leading-tight"
                onClick={() => deleteInputRow(index)}
                title="Delete Token"
              >
                <DeleteIcon darkMode={true} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    )
  }, [
    inputRows,
    handleSensorTypeChange,
    handleisPublicChange,
    handleEditChange,
    updateSensorDetails,
    cancelEdit,
    deleteInputRow,
  ])

  return (
    <div className={`h-full flex flex-col w-full  overflow-y-hidden`}>
      {/*flex flex-col w-full bg-white items-center*/}
      <MainNavMenu />
      <div
        className={`flex h-full justify-start items-start mx-auto p-10 md:mt-[-12px] text-base `}
      >
        {/*flex flex-row mx-8 basis-full justify-center style={{ maxWidth: '1280px' }} w-1/2*/}
        <div className="flex flex-col rounded-2xl self-center h-full">
          <div className="flex items-center self-start pt-2 mt-12 md:mt-4 h-6 justify-self-start">
            <img src={messagealert} className="w-10 h-10" />
            <p className="text-sm font-normal text-gray-600">
              {' '}
              If you are authorized to access sensors, please press the “Edit”
              button to enter the Device ID(s)/Token(s). This will automatically
              add those sensors to your observation map.
            </p>
          </div>
          <div className="h-3/4 overflow-x-auto">
            <div className="relative overflow-x-auto min-h-full w-full self-start justify-self-start">
              <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                <thead className="text-xs text-gray-700 uppercase dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-6 py-3">
                      Sensor Type
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Token
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Shared
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Edit
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Delete
                    </th>
                  </tr>
                </thead>
                {!isLoading && renderTableRows()}
                {isLoading && <SensorTableSkeleton rows={5} />}
              </table>
            </div>
          </div>
          <div className="flex flex-row justify-between w-full mt-5">
            <button
              className="button mr-4 text-center text-white border-gray-300 border rounded-md w-48 h-10 bg-green-500 hover:bg-green-700"
              onClick={getTempestToken}
            >
              Get Tempest Token
            </button>
            <button
              className="button mr-4 text-center text-white bg-green-500 hover:bg-green-700 rounded-md w-40 h-10"
              onClick={addNewSensor}
            >
              Add New Sensor
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SensorDetails;
